import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import type {
  ClaudeSession,
  DraftType,
  UserInputDraft,
} from '@/features/events/conversation/state/types'
import { DraftTimestamps } from './DraftTimestamps'

interface UserInputDraftItemProps {
  readonly draft: UserInputDraft
  readonly session: ClaudeSession
  readonly onDraftClick: (draft: UserInputDraft, session: ClaudeSession) => void
}

const DRAFT_TYPE_LABELS: Record<DraftType, string> = {
  user_reply: 'User reply',
  injected_prompt: 'Injected prompt',
  custom_option: 'User option',
  custom_answer: 'User answer',
  resume_session: 'Resume session',
}

export const UserInputDraftItem: FC<UserInputDraftItemProps> = ({
  draft,
  session,
  onDraftClick,
}) => {
  const theme = useTheme()

  const handleClick = useCallback(() => {
    onDraftClick(draft, session)
  }, [draft, session, onDraftClick])

  const labelColor = resolveDraftLabelColor(draft.type, theme)

  return (
    <Box sx={containerSx} onClick={handleClick}>
      <Typography component="span" sx={labelSx(labelColor)}>
        {DRAFT_TYPE_LABELS[draft.type]}
      </Typography>
      <DraftTimestamps createdAt={draft.createdAt} updatedAt={draft.updatedAt} />
    </Box>
  )
}

function resolveDraftLabelColor(type: DraftType, theme: Theme): string {
  switch (type) {
    case 'user_reply':
      return theme.palette.success.main
    case 'injected_prompt':
      return theme.palette.primary.main
    case 'custom_option':
      return theme.palette.warning.main
    case 'custom_answer':
      return theme.palette.warning.main
    case 'resume_session':
      return theme.palette.info.main
  }
}

const containerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 0.75,
  px: 2,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  borderRadius: 0.5,
}

const labelSx = (color: string): SxProps<Theme> => ({
  fontSize: '0.8125rem',
  color,
  fontWeight: 500,
})
